import type { FC, ReactNode, Ref } from "react";
import { createContext, use, useState } from "react";
import { Link as RouterLink } from "react-router";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import { Box, Divider, Link, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import type { TranslationKey } from "@privacy/translations";
import { Translate, useTranslation } from "@privacy/translations";
import { z } from "zod";
import type { AssistantInvitation, AssistantInvitationStatus } from "../../api/assistantInvitations";
import {
  CREATE_ASSISTANT_INVITATION_ERROR_CODES,
  assistantInvitationsQueryOptions,
  createAssistantInvitationMutationOptions,
  revokeAssistantInvitationMutationOptions,
} from "../../api/assistantInvitations";
import { hasErrorCode } from "../../api/errors";
import { DynamicGrid } from "../../dynamicGrid";
import { Drawer } from "../../shared/Drawer";
import * as FormDialog from "../../shared/FormDialog";
import { ActionMenu, ActionMenuItemWithPermission } from "../../shared/ActionMenu";
import { ConfirmDeleteDialog } from "../../shared/ConfirmDeleteDialog";
import { Grid } from "../../shared/Grid";
import { CircleXIcon, MailIcon, PersonIcon2 } from "../../shared/icons";
import { multilineEllipsis } from "../../shared/multilineEllipsis";
import { statusColors } from "../../shared/RegistrationStatus";
import { StatusChip } from "../../shared/StatusChip";
import type { Colors } from "../../shared/StatusChip";
import { usePermission } from "../../shared/permissions";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";

const REASON_MAX_LENGTH = 1000;

const EMAIL_SEPARATORS = /[\s,;]+/;

const EXPIRY_OPTIONS: Array<{ hours: number; labelKey: TranslationKey }> = [
  { hours: 1, labelKey: "Assistant.Invite.Dialog.Expiry.OneHour" },
  { hours: 4, labelKey: "Assistant.Invite.Dialog.Expiry.FourHours" },
  { hours: 24, labelKey: "Assistant.Invite.Dialog.Expiry.OneDay" },
  { hours: 72, labelKey: "Assistant.Invite.Dialog.Expiry.ThreeDays" },
  { hours: 168, labelKey: "Assistant.Invite.Dialog.Expiry.OneWeek" },
];

const INVITATION_STATUSES: Record<AssistantInvitationStatus, { labelKey: TranslationKey; colors: Colors }> = {
  waiting: { labelKey: "Assistant.Invite.Status.Waiting", colors: statusColors.draft },
  active: { labelKey: "Assistant.Invite.Status.Active", colors: statusColors.review },
  submitted: { labelKey: "Assistant.Invite.Status.Submitted", colors: statusColors.done },
  expired: { labelKey: "Assistant.Invite.Status.Expired", colors: statusColors.audit },
  revoked: { labelKey: "Assistant.Invite.Status.Revoked", colors: statusColors.audit },
};

const isOpenStatus = (status: AssistantInvitationStatus) => status === "waiting" || status === "active";

const isEmail = (value: string) => z.email().safeParse(value).success;

type InviteDrawerState = { isOpen: boolean; open: () => void; close: () => void };

const InviteDrawerContext = createContext<InviteDrawerState | null>(null);

export const AssistantInviteDrawerProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <InviteDrawerContext value={{ isOpen, open: () => setIsOpen(true), close: () => setIsOpen(false) }}>
      {children}
    </InviteDrawerContext>
  );
};

const useAssistantInviteDrawer = () => use(InviteDrawerContext);

export const AssistantInviteButton: FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const drawer = useAssistantInviteDrawer();

  if (!drawer || !hasPermission("nova-forms-api:registration:invite")) return null;

  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<MailIcon size={14} />}
      onClick={drawer.open}
      sx={{ flexShrink: 0 }}
    >
      {t("Assistant.Invite.Button")}
    </Button>
  );
};

export const AssistantInviteDrawer: FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const drawer = useAssistantInviteDrawer();

  if (!drawer || !hasPermission("nova-forms-api:registration:invite")) return null;

  return (
    <DynamicGrid.Drawer isOpen={drawer.isOpen} onClose={drawer.close} sx={{ pb: 0 }}>
      {drawer.isOpen && (
        <>
          <Drawer.Header title={t("Assistant.Invite.Drawer.Header")} status="hidden" onClose={drawer.close} />
          <Stack sx={{ minHeight: "calc(100% - 5rem)" }}>
            <Drawer.Content sx={{ pb: 4 }}>
              <InviteForm />
            </Drawer.Content>
            <Divider />
            <InvitationGrid />
          </Stack>
        </>
      )}
    </DynamicGrid.Drawer>
  );
};

type InviteFormValues = { emails: Array<string>; reason: string; expiresInHours: number };

const InviteForm: FC = () => {
  const { t } = useTranslation();
  const createInvitationMutation = useMutation(createAssistantInvitationMutationOptions());
  const [isSending, setIsSending] = useState(false);
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    setError,
    formState: { errors },
  } = useForm<InviteFormValues>({ defaultValues: { emails: [], reason: "", expiresInHours: 24 } });

  const send = async ({ emails, reason, expiresInHours }: InviteFormValues) => {
    setIsSending(true);
    const results = await Promise.allSettled(
      emails.map(email => createInvitationMutation.mutateAsync({ email, reason: reason.trim(), expiresInHours }))
    );
    setIsSending(false);

    const failed = emails.filter((_, index) => results[index]?.status === "rejected");
    if (failed.length === 0) return reset();

    const errorsOfFailed = results.flatMap(result => (result.status === "rejected" ? [result.reason] : []));
    setValue("emails", failed);
    if (errorsOfFailed.some(error => hasErrorCode(error, CREATE_ASSISTANT_INVITATION_ERROR_CODES.invalidEmail)))
      return setError("emails", { message: t("Assistant.Invite.Drawer.EmailsError") });
    if (errorsOfFailed.some(error => hasErrorCode(error, CREATE_ASSISTANT_INVITATION_ERROR_CODES.invalidReason)))
      return setError("reason", { message: t("Assistant.Invite.Dialog.ReasonError") });
    if (errorsOfFailed.some(error => hasErrorCode(error, CREATE_ASSISTANT_INVITATION_ERROR_CODES.invalidExpiry)))
      return setError("expiresInHours", { message: t("Assistant.Invite.Dialog.ExpiryError") });
    setError("root.serverError", {
      message: t("Assistant.Invite.Drawer.NotSentTo", { emails: failed.join(", ") }),
    });
  };

  return (
    <Box component="form" noValidate onSubmit={handleSubmit(send)}>
      <FormDialog.FormError
        isError={Boolean(errors.root?.serverError)}
        errorMessage={errors.root?.serverError?.message}
      />
      <FormDialog.Fields>
        <FormDialog.ControlledField
          control={control}
          name="emails"
          rules={{
            validate: value => (value.length > 0 && value.every(isEmail)) || t("Assistant.Invite.Drawer.EmailsError"),
          }}
          label={t("Assistant.Invite.Drawer.EmailsLabel")}
        >
          {({ field: { ref, value, onChange, onBlur }, fieldState, id, errorId }) => (
            <EmailChipsInput
              id={id}
              inputRef={ref}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              isError={Boolean(fieldState.error)}
              errorId={errorId}
              placeholder={t("Assistant.Invite.Drawer.EmailsPlaceholder")}
            />
          )}
        </FormDialog.ControlledField>
        <FormDialog.TextField
          control={control}
          name="reason"
          rules={{ validate: value => value.trim().length > 0 || t("Assistant.Invite.Dialog.ReasonError") }}
          label={t("Assistant.Invite.Dialog.ReasonLabel")}
          placeholder={t("Assistant.Invite.Dialog.ReasonPlaceholder")}
          multiline
          minRows={5}
          slotProps={{ htmlInput: { maxLength: REASON_MAX_LENGTH } }}
        />
        <FormDialog.ControlledField control={control} name="expiresInHours" label={t("Assistant.Invite.Dialog.Expiry")}>
          {({ field: { ref, ...field }, id }) => (
            <Select {...field} inputRef={ref} id={id} size="small" fullWidth>
              {EXPIRY_OPTIONS.map(option => (
                <MenuItem key={option.hours} value={option.hours}>
                  {t(option.labelKey)}
                </MenuItem>
              ))}
            </Select>
          )}
        </FormDialog.ControlledField>
        <Box>
          <FormDialog.SubmitButton isSubmitting={isSending}>
            {t("Assistant.Invite.Dialog.Submit")}
          </FormDialog.SubmitButton>
        </Box>
      </FormDialog.Fields>
    </Box>
  );
};

type EmailChipsInputProps = {
  id: string;
  inputRef: Ref<HTMLInputElement>;
  value: Array<string>;
  onChange: (value: Array<string>) => void;
  onBlur: () => void;
  isError: boolean;
  errorId: string | undefined;
  placeholder: string;
};

const EmailChipsInput: FC<EmailChipsInputProps> = ({
  id,
  inputRef,
  value,
  onChange,
  onBlur,
  isError,
  errorId,
  placeholder,
}) => {
  const [inputValue, setInputValue] = useState("");

  const add = (text: string) => {
    const added = text
      .split(EMAIL_SEPARATORS)
      .map(email => email.trim())
      .filter(Boolean);
    if (added.length > 0) onChange([...new Set([...value, ...added])]);
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      id={id}
      options={[]}
      value={value}
      inputValue={inputValue}
      clearIcon={null}
      onChange={(_, emails) => onChange([...new Set(emails.flatMap(email => email.split(EMAIL_SEPARATORS)))])}
      onInputChange={(_, text, reason) => {
        if (reason === "input" && EMAIL_SEPARATORS.test(text)) {
          add(text);
          return setInputValue("");
        }
        setInputValue(text);
      }}
      onBlur={() => {
        add(inputValue);
        setInputValue("");
        onBlur();
      }}
      renderValue={(emails, getItemProps) =>
        emails.map((email, index) => {
          const { key, ...itemProps } = getItemProps({ index });
          return (
            <Chip key={key} {...itemProps} size="small" label={email} color={isEmail(email) ? "default" : "error"} />
          );
        })
      }
      renderInput={params => (
        <TextField
          {...params}
          inputRef={inputRef}
          size="small"
          error={isError}
          placeholder={value.length === 0 ? placeholder : undefined}
          slotProps={{
            ...params.slotProps,
            htmlInput: { ...params.slotProps.htmlInput, "aria-describedby": errorId },
          }}
        />
      )}
    />
  );
};

const InvitationGrid: FC = () => {
  const { t } = useTranslation();
  const invitationsQuery = useQuery(assistantInvitationsQueryOptions());

  return (
    <Stack sx={{ flex: 1, minHeight: "24rem" }}>
      <Typography
        sx={{ px: Drawer.PADDING_X, pt: 4, pb: 2, fontWeight: 600, fontSize: theme => theme.typography.pxToRem(16) }}
      >
        {t("Assistant.Invite.Title")}
        {invitationsQuery.data && (
          <Typography component="span" sx={{ ml: 1, fontSize: "inherit" }}>
            ({invitationsQuery.data.length})
          </Typography>
        )}
      </Typography>
      <Drawer.Grid
        showColumnHeaders
        loading={invitationsQuery.isPending}
        getRowId={row => row.invitationId}
        rows={invitationsQuery.data ?? []}
        slots={{ toolbar: Drawer.GridToolbar, noRowsOverlay: NoInvitationsOverlay }}
        columns={[
          {
            field: "email",
            flex: 1,
            minWidth: 220,
            headerName: t("Assistant.Invite.Grid.Colleague"),
            valueGetter: (_, { email, reason }) => `${email} ${reason}`,
            renderCell: ({ row }) => <ColleagueCell invitation={row} />,
          },
          {
            field: "status",
            width: 140,
            headerName: t("Assistant.Invite.Grid.Status"),
            valueGetter: (_, { status }) => t(INVITATION_STATUSES[status].labelKey),
            renderCell: ({ row }) => (
              <Drawer.GridCell>
                <StatusChip
                  colors={INVITATION_STATUSES[row.status].colors}
                  labelKey={INVITATION_STATUSES[row.status].labelKey}
                />
              </Drawer.GridCell>
            ),
          },
          {
            field: "expiresAt",
            width: 170,
            headerName: t("Assistant.Invite.Grid.ExpiresAt"),
            renderCell: ({ row }) => <DateCell date={row.expiresAt} />,
          },
          {
            field: Grid.ACTIONS_COLUMN_FIELD,
            width: 64,
            sortable: false,
            headerName: "",
            renderCell: ({ row }) =>
              isOpenStatus(row.status) ? (
                <Drawer.GridCell align="center">
                  <InvitationActionMenu invitation={row} />
                </Drawer.GridCell>
              ) : null,
          },
        ]}
      />
    </Stack>
  );
};

const NoInvitationsOverlay = () => <Drawer.GridNoRowsOverlay labelKey="Assistant.Invite.Drawer.Empty" />;

const ColleagueCell: FC<{ invitation: AssistantInvitation }> = ({ invitation }) => {
  const { t, formatDate } = useTranslation();
  const { paths } = usePrivacyNavigate();

  return (
    <Drawer.GridCell>
      <Stack direction="row" sx={{ gap: 2 }}>
        <PersonIcon2 size="sm" />
        <Stack sx={{ minWidth: 0, gap: 0.5 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: theme => theme.typography.pxToRem(14),
              lineHeight: 1.2,
              overflowWrap: "anywhere",
            }}
          >
            {invitation.submittedRecordId ? (
              <Link
                component={RouterLink}
                to={paths.registrationsInline({ id: invitation.submittedRecordId, variant: "Controller" })}
              >
                {invitation.email}
              </Link>
            ) : (
              invitation.email
            )}
          </Typography>
          <Typography
            title={invitation.reason}
            sx={{
              fontSize: theme => theme.typography.pxToRem(12),
              lineHeight: 1.2,
              color: theme => theme.palette.text.secondary,
              ...multilineEllipsis(2),
            }}
          >
            {invitation.reason}
          </Typography>
          <Typography
            sx={{
              fontSize: theme => theme.typography.pxToRem(12),
              lineHeight: 1.2,
              color: theme => theme.palette.text.tertiary,
            }}
          >
            {invitation.status === "submitted" && invitation.submittedAt
              ? t("Assistant.Invite.SubmittedAt", { time: formatDate(invitation.submittedAt, { showTime: true }) })
              : t("Assistant.Invite.InvitedAt", { time: formatDate(invitation.createdAt, { showTime: true }) })}
          </Typography>
        </Stack>
      </Stack>
    </Drawer.GridCell>
  );
};

const DateCell: FC<{ date: string }> = ({ date }) => {
  const { formatDate } = useTranslation();

  return (
    <Drawer.GridCell>
      <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14), lineHeight: 1.2 }}>
        {formatDate(date, { showTime: true })}
      </Typography>
    </Drawer.GridCell>
  );
};

const InvitationActionMenu: FC<{ invitation: AssistantInvitation }> = ({ invitation }) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const revokeMutation = useMutation(revokeAssistantInvitationMutationOptions());

  const closeConfirm = () => {
    setIsConfirmOpen(false);
    setIsMenuOpen(false);
  };

  return (
    <>
      <ActionMenu isOpen={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <ActionMenuItemWithPermission onClick={() => setIsConfirmOpen(true)}>
          <CircleXIcon />
          {t("Assistant.Invite.Revoke")}
        </ActionMenuItemWithPermission>
      </ActionMenu>
      <ConfirmDeleteDialog
        isOpen={isConfirmOpen}
        onDelete={() => revokeMutation.mutateAsync(invitation.invitationId)}
        onClose={closeConfirm}
        headerText={t("Assistant.Invite.RevokeDialog.Header")}
        message={
          <Translate
            translationKey="Assistant.Invite.RevokeDialog.Message"
            values={{
              email: (
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  {invitation.email}
                </Box>
              ),
            }}
          />
        }
        confirmButtonLabel={t("Assistant.Invite.Revoke")}
        id="confirm-revoke-invitation"
      />
    </>
  );
};
